import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Anomaly {
  id: string
  metric: string
  detected_value: number
  expected_value: number
  deviation_percent: number
  severity: string
  status: string
  root_cause: string
  impact_estimate: number
}

interface Detector {
  id: string
  name: string
  type: string
  status: string
  metrics_monitored: number
  sensitivity: number
  false_positive_rate: number
}

interface Statistics {
  total_anomalies_detected: number
  open_anomalies: number
  critical_anomalies: number
  active_detectors: number
  metrics_monitored: number
  avg_detection_time_sec: number
  false_positive_rate: number
  resolution_time_hours: number
}

export default function AnomalyDetectionPage() {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([])
  const [detectors, setDetectors] = useState<Detector[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'anomalies' | 'detectors'>('anomalies')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [anomRes, detRes, statsRes] = await Promise.all([
        fetch('/api/anomalydetection/anomalies'),
        fetch('/api/anomalydetection/detectors'),
        fetch('/api/anomalydetection/statistics')
      ])

      if (anomRes.ok) setAnomalies(await anomRes.json())
      if (detRes.ok) setDetectors(await detRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching anomaly detection data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSeverityBadge = (severity: string) => {
    const classes: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    }
    return classes[severity] || 'bg-gray-100 text-gray-800'
  }

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      open: 'bg-red-100 text-red-800',
      investigating: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      training: 'bg-blue-100 text-blue-800',
      disabled: 'bg-gray-100 text-gray-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Anomaly Detection</h1>
          <p className="mt-2 text-gray-600">AI-powered anomaly detection and alerting</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Anomalies</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.open_anomalies}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-2xl font-bold text-red-600">{statistics.critical_anomalies}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Detectors</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_detectors}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Metrics Monitored</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.metrics_monitored}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('anomalies')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'anomalies' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Anomalies ({anomalies.length})
            </button>
            <button
              onClick={() => setActiveTab('detectors')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'detectors' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Detectors ({detectors.length})
            </button>
          </nav>
        </div>

        {activeTab === 'anomalies' && (
          <div className="space-y-4">
            {anomalies.map((anomaly) => (
              <div key={anomaly.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">{anomaly.metric.replace('_', ' ')}</h3>
                    <p className="text-sm text-gray-500 capitalize mt-1">{anomaly.root_cause.replace('_', ' ')}</p>
                  </div>
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(anomaly.severity)}`}>
                      {anomaly.severity}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(anomaly.status)}`}>
                      {anomaly.status}
                    </span>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Detected</p>
                    <p className="text-lg font-bold text-red-600">{anomaly.detected_value.toFixed(1)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Expected</p>
                    <p className="text-lg font-bold text-gray-900">{anomaly.expected_value.toFixed(1)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Deviation</p>
                    <p className="text-lg font-bold text-orange-600">{anomaly.deviation_percent.toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Impact</p>
                    <p className="text-lg font-bold text-purple-600">${anomaly.impact_estimate.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'detectors' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detector</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metrics</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sensitivity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">FP Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {detectors.map((det) => (
                  <tr key={det.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{det.name}</td>
                    <td className="px-6 py-4 text-gray-500 capitalize">{det.type}</td>
                    <td className="px-6 py-4 text-gray-500">{det.metrics_monitored}</td>
                    <td className="px-6 py-4 text-gray-500">{(det.sensitivity * 100).toFixed(0)}%</td>
                    <td className="px-6 py-4 text-gray-500">{(det.false_positive_rate * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(det.status)}`}>
                        {det.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
